import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { TopNav } from '../components/layout/TopNav';
import { BottomNav } from '../components/layout/BottomNav';
import { EBTWICBanner } from '../components/layout/EBTWICBanner';
import { HeroSection } from '../components/sections/HeroSection';
import { ProductCard } from '../components/product/ProductCard';
import { RecipeBundle } from '../components/product/RecipeBundle';
import { mockProducts, recipeBundles, getProductById } from '../data/mockProducts';
export function HomePage() {
  const featuredProducts = mockProducts.slice(0, 8);
  const wicProducts = mockProducts.filter(p => p.isWIC).slice(0, 4);
  const bundles = recipeBundles.map(bundle => {
    const products = bundle.productIds.map(id => getProductById(id)).filter((p): p is NonNullable<typeof p> => p !== undefined);
    const totalPrice = products.reduce((sum, p) => sum + p.price, 0);
    return {
      ...bundle,
      products,
      totalPrice
    };
  });
  return <div className="min-h-screen bg-[#FAF8F5] pb-20 md:pb-0" data-id="element-304">
      <TopNav data-id="element-305" />
      <EBTWICBanner data-id="element-306" />
      <HeroSection data-id="element-307" />

      {/* Featured Products */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16" data-id="element-308">
        <div className="flex items-center justify-between mb-8" data-id="element-309">
          <div data-id="element-310">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900" data-id="element-311">
              Featured Products
            </h2>
            <p className="text-gray-600 mt-1" data-id="element-312">
              Fresh arrivals and customer favorites
            </p>
          </div>
          <Link to="/search" className="hidden md:flex items-center gap-2 text-[#948B7A] font-semibold hover:underline" data-id="element-313">
            View All
            <ArrowRight className="w-4 h-4" data-id="element-314" />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-id="element-315">
          {featuredProducts.map((product, index) => <motion.div key={product.id} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.05
        }} data-id="element-316">
              <ProductCard product={product} data-id="element-317" />
            </motion.div>)}
        </div>

        <Link to="/search" className="md:hidden mt-6 flex items-center justify-center gap-2 text-[#948B7A] font-semibold" data-id="element-318">
          View All Products
          <ArrowRight className="w-4 h-4" data-id="element-319" />
        </Link>
      </section>

      {/* Recipe Bundles */}
      <section className="bg-white py-12 md:py-16" data-id="element-320">
        <div className="max-w-7xl mx-auto px-4 md:px-6" data-id="element-321">
          <div className="flex items-center gap-3 mb-2" data-id="element-322">
            <div className="bg-[#D97757]/10 p-2 rounded-lg" data-id="element-323">
              <Sparkles className="w-6 h-6 text-[#D97757]" data-id="element-324" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900" data-id="element-325">
              Recipe Bundles
            </h2>
          </div>
          <p className="text-gray-600 mb-8" data-id="element-326">
            Save time and money with our curated ingredient bundles
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-327">
            {bundles.map((bundle, index) => <motion.div key={bundle.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} data-id="element-328">
                <RecipeBundle title={bundle.title} description={bundle.description} products={bundle.products} totalPrice={bundle.totalPrice} savings={bundle.savings} data-id="element-329" />
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* WIC Approved Section */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16" data-id="element-330">
        <div className="bg-gradient-to-br from-[#047857] to-[#059669] rounded-3xl p-6 md:p-10 text-white" data-id="element-331">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8" data-id="element-332">
            <div data-id="element-333">
              <h2 className="text-2xl md:text-3xl font-bold mb-2" data-id="element-334">
                WIC Approved Products
              </h2>
              <p className="text-white/80" data-id="element-335">
                Shop our selection of WIC-eligible items for your family
              </p>
            </div>
            <Link to="/search?filter=wic" className="inline-flex items-center gap-2 bg-white text-[#047857] px-6 py-3 rounded-xl font-bold hover:bg-gray-100 transition-colors" data-id="element-336">
              View All WIC Items
              <ArrowRight className="w-4 h-4" data-id="element-337" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-338">
            {wicProducts.map(product => <ProductCard key={product.id} product={product} data-id="element-339" />)}
          </div>
        </div>
      </section>

      {/* Store Info */}
      <section className="bg-[#1E3A5F] text-white py-12 md:py-16" data-id="element-340">
        <div className="max-w-7xl mx-auto px-4 md:px-6" data-id="element-341">
          <div className="grid md:grid-cols-3 gap-8" data-id="element-342">
            <div data-id="element-343">
              <h3 className="text-xl font-bold mb-4" data-id="element-344">Visit Our Store</h3>
              <p className="text-white/80 mb-2" data-id="element-345">13535 Clopper Rd</p>
              <p className="text-white/80 mb-4" data-id="element-346">Germantown, MD 20874</p>
              <p className="text-[#F4A261] font-semibold" data-id="element-347">Mon-Sat: 9am-9pm</p>
            </div>
            <div data-id="element-348">
              <h3 className="text-xl font-bold mb-4" data-id="element-349">Contact Us</h3>
              <p className="text-white/80 mb-2" data-id="element-350">Phone: (240) 389-7863</p>
              <p className="text-white/80" data-id="element-351">Email: info@naanfemarket.com</p>
            </div>
            <div data-id="element-352">
              <h3 className="text-xl font-bold mb-4" data-id="element-353">Delivery Partners</h3>
              <div className="flex flex-wrap gap-2" data-id="element-354">
                {['Uber Eats', 'DoorDash', 'Instacart', 'GrubHub'].map(partner => <span key={partner} className="bg-white/10 px-3 py-1.5 rounded-lg text-sm font-medium" data-id="element-355">
                      {partner}
                    </span>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      <BottomNav data-id="element-356" />
    </div>;
}